// TASK-140: the admin SPA's composition root. `ROUTES` concatenates every screen module's own
// `routes` (each tied to its NAV-00x node and permission, router/route.ts's `ScreenRoute`) into the
// one table `router/router.ts`'s `resolve`/`navEntries` read; `main.tsx` renders against it and the
// navigation shows only the NAV nodes `navEntries` admits for the signed-in session. Pure and
// tested here (`tests/unit/app-routes.test.ts`) so the table and the landing choice below can be
// checked without a DOM — `main.tsx` and `ui/shell.tsx` just consume them (constraint 35).
import { HOME_PATH } from "./screens/home/model";
import { routes as auditRoutes } from "./screens/audit";
import { routes as billingRoutes } from "./screens/billing";
import { routes as expenseRoutes } from "./screens/expense";
import { routes as helpRoutes } from "./screens/help";
import { routes as homeRoutes } from "./screens/home";
import { routes as installerRoutes } from "./screens/installer";
import { routes as invoiceRoutes } from "./screens/invoice";
import { routes as notificationsRoutes } from "./screens/notifications";
import { routes as orgsRoutes } from "./screens/orgs";
import { routes as pricesRoutes } from "./screens/prices";
import { routes as quotasRoutes } from "./screens/quotas";
import { routes as ratePlansRoutes } from "./screens/rateplans";
import { routes as reconciliationRoutes } from "./screens/reconciliation";
import { routes as revenueRoutes } from "./screens/revenue";
import { routes as signinRoutes } from "./screens/signin";
import { routes as termsRoutes } from "./screens/terms";
import type { ScreenRoute } from "./router/route";
import type { Session } from "./session/session";

// billing (periods list, TASK-110), rate-plans/terms (commercial terms, TASK-113) and invoice
// (TASK-121) deliberately share the `/billing` and `/rate-plans` prefixes at different segment
// counts (`/billing`, `/billing/:orgId/:period`, `/billing/:orgId/:period/adjustments`); the
// router's specificity ordering (router/router.ts's `matchPath`/`compareSpecificity`) resolves
// each address to exactly the route that owns it — see tests/unit/router.test.ts's route-table
// tests and this module's own. Every customer tab, Quotas (TASK-257) and Notifications included,
// renders inside the customer page at `/customers/:orgId/:tab[/:subtab]` (REQ-238; on Quotas the
// segment is a quota's id); the fleet view `/quotas` (NAV-019) is a separate route, staff only.
export const ROUTES: readonly ScreenRoute[] = [
  ...signinRoutes,
  ...homeRoutes,
  ...orgsRoutes,
  ...quotasRoutes,
  ...notificationsRoutes,
  ...pricesRoutes,
  ...billingRoutes,
  ...ratePlansRoutes,
  ...termsRoutes,
  ...invoiceRoutes,
  ...revenueRoutes,
  ...expenseRoutes,
  ...reconciliationRoutes,
  ...installerRoutes,
  ...auditRoutes,
  ...helpRoutes,
];

/**
 * FLOW-002's landing branches: "billing admin → lands on Billing; installer operator → lands on
 * Installer" — everyone else (a super-admin, an org-admin, or the happy path's own default before
 * either branch applies) lands on Home (NAV-002), where the tiles and full navigation are. Checked
 * by permission, not role, so a service-shaped platform principal holding `billing:read` lands the
 * same place a dedicated billing admin would; a super-admin is excluded first because `Session.can`
 * grants it every permission (ADR-031), which would otherwise always take the billing branch.
 */
export function landingPath(session: Session): string {
  if (session.me.role === "super-admin") return HOME_PATH;
  if (session.can("billing:read")) return "/invoices";
  if (session.can("installer:run")) return "/installer";
  return HOME_PATH;
}
